import { addMinutes, format, isValid, parseISO, setHours, setMinutes, subMinutes } from 'date-fns'
import { DateTime } from 'luxon'
import { Matcher } from 'react-day-picker'

import i18n from '../../../../apps/backoffice/i18n'
import { Time } from '../../../../apps/backoffice/src/lib/types/utils'
import { ClockType, TimeType } from '../types'
import { DisabledDatesOptions, YearOptionsType } from '../types/date.type'
import { StringFormatter } from './string.utils'

/**
 * Returns the locale to use when formatting a date.
 *
 * Dates are always displayed day-first (`dd/mm/yyyy`) whatever the UI
 * language, so the English UI is formatted with `en-GB` instead of the default `en`
 * (which is US-based and would render `08/13/2026`). Month and weekday names stay English.
 */
function getDateLocale(): string {
  const language = i18n.language
  return language === 'en' || language.startsWith('en-') ? 'en-GB' : language
}

export abstract class DateFormatter {
  /**
   * Formats a date to "DD/MM/YYYY".
   * @param date - The date to format.
   * @returns The formatted date.
   * @example
   * ```ts
   * const date = new Date("2021-01-01");
   * const formattedDate = DateFormatter.DDMMYYYY(date);
   * formattedDate; // "01/01/2021"
   * ```
   * @param options.i18n - When `false`, ignore the current UI language and always use the fixed "DD/MM/YYYY" format. Defaults to `true`.
   * @see {@link DateFormatter}
   */
  static DDMMYYYY(date: Date | string, options?: { i18n?: boolean }): string {
    const locale = options?.i18n === false ? 'fr-FR' : getDateLocale()
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(new Date(date))
  }

  /**
   * Formats a date to "DD/MM".
   * @param date - The date to format.
   * @returns The formatted date.
   * @example
   * ```ts
   * const date = new Date("2021-01-01");
   * const formattedDate = DateFormatter.DDMM(date);
   * formattedDate; // "01/01"
   * ```
   * @see {@link DateFormatter}
   */
  static DDMM(date: Date | string): string {
    return new Intl.DateTimeFormat(getDateLocale(), {
      day: '2-digit',
      month: '2-digit'
    }).format(new Date(date))
  }

  static DDmm(date: Date | string): string {
    const formatter = new Intl.DateTimeFormat(i18n.language, {
      month: 'short',
      day: '2-digit'
    })
    const parts = formatter.formatToParts(new Date(date))

    // Extract the day and month parts
    const day = parts.find((part) => part.type === 'day')?.value
    const month = parts.find((part) => part.type === 'month')?.value

    // Rearrange to "month day"
    return `${month?.slice(0, 1).toUpperCase()}${month?.slice(1, 3)} ${day}`
  }

  static HHmm(date: Date | string): string {
    return new Date(date).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    })
  }

  /**
   * Formats a date to "HHhMM".
   * @param date - The date to format.
   * @returns The formatted date.
   * @example
   * ```ts
   * const date = new Date("2021-01-01T12:00:00");
   * const formattedDate = DateFormatter.HHhMM(date);
   * formattedDate; // "12h00"
   * ```
   */
  static HHhMM(date: Date | string): string {
    return DateFormatter.HHmm(new Date(date)).replace(':', 'h')
  }

  static HH(hour?: number): string {
    if (hour === 0) return '00'
    if (!hour) return ' - '
    return hour.toString().padStart(2, '0')
  }

  static MM(minute?: number): string {
    if (minute === 0) return '00'
    if (!minute) return ' - '
    return minute.toString().padStart(2, '0')
  }

  /**
   * Return the hours and minutes of a given date.
   * @param date - The date to get the hours and minutes from.
   * @returns The hours and minutes of the given date.
   * @example
   * ```ts
   * const date = new Date("2021-01-01T12:00:00");
   * const time = DateFormatter.toTime(date);
   * time; // "12h 00mins"
   * ```
   */
  static toTime(date: Date | string): string {
    const d = new Date(date)
    return `${d.getHours()}h ${d.getMinutes()}mins`
  }

  public static timeSince(d: Date | string) {
    const date = new Date(d)
    const now = new Date()
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    let interval = seconds / 31536000

    if (interval > 1) {
      return `${Math.floor(interval)}y`
    }
    interval = seconds / 2592000
    if (interval > 1) {
      return `${Math.floor(interval)}m`
    }
    interval = seconds / 86400
    if (interval > 1) {
      return `${Math.floor(interval)}d`
    }
    interval = seconds / 3600
    if (interval > 1) {
      return `${Math.floor(interval)}h`
    }
    interval = seconds / 60
    if (interval > 1) {
      return `${Math.floor(interval)}m`
    }
    return `${Math.floor(seconds)}s`
  }

  public static dateToText(date: Date): string {
    const today = new Date()
    const daysDiff = Math.floor((today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))

    const dayLabels: Record<number, string> = {
      0: "Aujourd'hui",
      1: 'Hier',
      2: 'Il y a 2 jours',
      3: 'Il y a 3 jours',
      4: 'Il y a 4 jours',
      5: 'Il y a 5 jours',
      6: 'Il y a 6 jours',
      7: 'Il y a 7 jours'
    }

    if (daysDiff >= 0 && daysDiff <= 7) {
      return dayLabels[daysDiff]
    } else if (daysDiff <= 14) {
      return 'La semaine passée'
    } else {
      return `${StringFormatter.capitalize(date.toLocaleDateString(getDateLocale(), { weekday: 'long' }))} ${DateFormatter.DDMMYYYY(date)}`
    }
  }

  /**
   * Calculates age from birthdate.
   * @param birthdate - The birthdate as a Date object or a string.
   * @returns The calculated age.
   */
  static calculateAge(birthdate: Date | string) {
    const birthDate = new Date(birthdate)
    const today = new Date()

    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDifference = today.getMonth() - birthDate.getMonth()
    const dayDifference = today.getDate() - birthDate.getDate()

    // If today's date is before the birthdate in the current year, subtract one from age
    if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
      age--
    }

    return age
  }

  /**
   * Calculate age based on birthdate and a reference date
   * @param {string|Date} birthDate - The birth date (format: MM/DD/YYYY or Date object)
   * @param {string|Date} referenceDate - The reference date to calculate age at (format: MM/DD/YYYY or Date object)
   * @returns {number} The age in years
   */
  static calculateRelativeAge(birthDate: Date | string, referenceDate: Date | string): number {
    // Convert string dates to Date objects if needed
    const birth = birthDate instanceof Date ? birthDate : new Date(birthDate)
    const reference = referenceDate instanceof Date ? referenceDate : new Date(referenceDate)

    // Calculate the difference in years
    let age = reference.getFullYear() - birth.getFullYear()

    // Check if birthday hasn't occurred yet in the reference year
    const birthMonth = birth.getMonth()
    const referenceMonth = reference.getMonth()

    if (
      referenceMonth < birthMonth ||
      (referenceMonth === birthMonth && reference.getDate() < birth.getDate())
    ) {
      age--
    }

    return age
  }

  /**
   * Returns a date range string in the format "DD/MM/YYYY - DD/MM/YYYY".
   * @param startDate - The start date.
   * @param endDate - The end date.
   * @returns The date range string.
   * @example
   * ```ts
   * const startDate = new Date("2025-02-01");
   * const endDate = new Date("2025-02-05");
   * const dateRange = DateFormatter.getDateRangeDDMMYYYY(startDate, endDate);
   * dateRange; // "01/02/2025 - 05/02/2025"
   * ```
   * @see {@link DateFormatter.DDMMYYYY}
   */
  static getDateRangeDDMMYYYY(startDate: Date | string, endDate: Date | string): string {
    return `${DateFormatter.DDMMYYYY(startDate)} - ${DateFormatter.DDMMYYYY(endDate)}`
  }

  /**
   * Checks if a date is within a specific day of the week and between a start and end date.
   * @param targetDate - The target date to check.
   * @param targetDay - The target day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday).
   * @param startDate - The start date of the range.
   * @param endDate - The end date of the range.
   * @returns `true` if the target date is within the specified day of the week and date range, `false` otherwise.
   * @example
   * ```ts
   * const targetDate = new Date("2021-01-01");
   * const targetDay = 5; // Friday
   * const startDate = new Date("2021-01-01");
   * const endDate = new Date("2021-01-31");
   * const isSameDay = DateFormatter.isSameDay(targetDate, targetDay, startDate, endDate);
   * isSameDay; // true
   * ```
   * @see {@link DateFormatter}
   */
  static isSameDay(targetDate: Date, targetDay: number, startDate: Date, endDate: Date): boolean {
    return (
      new Date(targetDate).getDay() === targetDay &&
      new Date(targetDate) >= startDate &&
      new Date(targetDate) < endDate
    )
  }

  /**
   * Converts a date to a date format string.
   * @param date - The date to convert.
   * @returns The date format string.
   * @example
   * ```ts
   * const date = new Date("2021-01-01");
   * const dateFormat = DateFormatter.toDateFormat(date);
   * dateFormat; // "2021-01-01"
   * ```
   */
  // static toDateFormat(date?: Date | string): string | undefined {
  //   if (!date) return undefined
  //   return format(date, 'yyyy-MM-dd')
  // }

  // Converts a date string to a Date object.
  // From ISO 8601 format (YYYY-MM-DD) to Date object
  static toDate(date: string): Date {
    return parseISO(date)
  }

  static toDateFormatted(date: Date): string {
    return format(date, 'yyyy-MM-dd')
  }

  // Check if the string is a valid date format (YYYY-MM-DD) - use date-fns
  static checkDateFormat(date?: string | null): string | undefined {
    if (!date) return undefined

    try {
      const parsed = parseISO(date)
      if (!isValid(parsed)) return undefined

      // Ensure the input was actually in YYYY-MM-DD format by re-formatting it
      return format(parsed, 'yyyy-MM-dd')
    } catch {
      return undefined
    }
  }

  static formatDateHour(date: Date | string): string {
    return new Intl.DateTimeFormat(getDateLocale(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date))
  }

  /**
   * Formats a date to a string in the format "DD/MM/YYYY HH:mm".
   * @param date - The date to format.
   * @returns The formatted date string.
   * @example
   * ```ts
   * const date = new Date("2021-01-01T12:00:00");
   * const formattedDate = DateFormatter.ddMMyyyyHHmm(date);
   * formattedDate; // "01/01/2021 12:00"
   * ```
   */
  static ddMMyyyyHHmm(date: Date | string): string {
    // add a separator between the date and time
    return new Intl.DateTimeFormat(getDateLocale(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(new Date(date))
  }

  /**
   * Formats a date to a string in the format "DD/MM/YYYY - HH:mm".
   * @param date - The date to format.
   * @returns The formatted date string.
   * @example
   * ```ts
   * const date = new Date("2021-01-01");
   * const formattedDate = DateFormatter.ddMMYYYY_HHMM(date);
   * formattedDate; // "12/07/2024 - 14:30"
   * ```
   */
  static ddMMYYYY_HHMM(date: Date | string): string {
    return `${DateFormatter.DDMMYYYY(date)} - ${DateFormatter.HHmm(date)}`
  }

  static setTimeToDate(date: Date | string, time: string): Date {
    const [hours, minutes] = time.split(':').map((part) => parseInt(part, 10))

    return new Date(new Date(date).setHours(hours ?? 0, minutes ?? 0))
  }

  static formatTimeString(hours?: number, minutes?: number): TimeType | undefined {
    if (hours == null && minutes == null) return undefined
    return `${this.HH(hours ?? 0)}:${this.MM(minutes ?? 0)}` as TimeType
  }

  /**
   * Returns the difference in months between two dates.
   * @param d1 - The first date.
   * @param d2 - The second date.
   * @returns The difference in months between the two dates.
   * @example
   * ```ts
   * const d1 = new Date("2021-01-01");
   * const d2 = new Date("2021-12-31");
   * const monthDiff = DateFormatter.monthDiff(d1, d2);
   * monthDiff // 11
   * ```
   * @see {@link DateFormatter}
   */
  static monthDiff(date1: Date, date2: Date): number {
    const d1 = new Date(date1)
    const d2 = new Date(date2)

    return d2.getMonth() - d1.getMonth() + 12 * (d2.getFullYear() - d1.getFullYear())
  }

  static yearDiff(date1: Date | string, date2: Date | string): number {
    // Get the difference in years in specific decimals
    const d1 = new Date(date1)
    const d2 = new Date(date2)
    const diffInDays = (d2.getTime() - d1.getTime()) / (1000 * 3600 * 24)
    return diffInDays / 365.25
  }

  /**
   * Returns the year of a given date.
   * @param date - The date to get the year from.
   * @returns The year of the given date.
   * @example
   * ```ts
   * const date = new Date("2021-01-01");
   * const year = DateFormatter.toYear(date);
   * year; // 2021
   * ```
   * @see {@link DateFormatter}
   */
  static toYear(date: Date | string) {
    return new Date(date).getFullYear()
  }

  /**
   * Calculates the difference in years between 2 given dates.
   * @param date1 - The first date to calculate the difference from.
   * @param date2 - The second date to calculate the difference to.
   * @returns The difference in years.
   * @example
   * ```ts
   * const createdAt = "2015-05-15";
   * const years = DateFormatter.getYearsDifference(createdAt, new Date());
   * years; // 9 (if the current year is 2024)
   * ```
   * @see {@link DateFormatter.getYearsDifference}
   */
  static getYearsDifference(date1: Date | string, date2: Date | string): number {
    const d1 = new Date(date1)
    const d2 = new Date(date2)

    return d1.getFullYear() - d2.getFullYear()
  }

  /**
   * Returns the date in the format "Day DD/MM/YYYY".
   * @param date - The date to format.
   * @returns The current date and time.
   * @example
   * ```ts
   * const date = new Date("2021-01-01T12:00:00");
   * const formattedDate = DateFormatter.DDDMYYYY(date);
   * formattedDate; // "Jeudi 01/07/2021"
   * ```
   */
  static DDDMYYYY(d: Date | string): string {
    const date = typeof d === 'string' ? new Date(d) : d
    const day =
      date.toLocaleString(i18n.language, { weekday: 'long' }).charAt(0).toUpperCase() +
      date.toLocaleString(i18n.language, { weekday: 'long' }).slice(1)

    return `${day} ${DateFormatter.DDMMYYYY(date)}`
  }

  /**
   * Returns the day name of a date
   * @param date - The date to get the day name of
   * @returns date formated as "Jeudi 12/07/2025"
   * @example
   * ```ts
   * const dayName = getDayName('2025-07-12')
   * dayName // "Jeudi 12/07/2025"
   * ```
   */
  static getDayName(date: Date | string): string {
    const formattedDate = new Date(date).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'numeric',
      year: 'numeric'
    })
    return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1)
  }

  static get hours() {
    const hours = Array.from({ length: 24 }, (_, i) => i)
    return hours.flatMap((hour) =>
      [0, 15, 30, 45].map((minute) => ({
        label: `${hour.toString().padStart(2, '0')} : ${minute.toString().padStart(2, '0')}`,
        value: `${hour}:${minute.toString().padStart(2, '0')}`
      }))
    )
  }

  static get timezones() {
    return Array.from({ length: 25 }, (_, i) => {
      const offset = i - 12 // UTC offsets range from -12 to +12
      const formattedOffset = offset >= 0 ? `+${offset}` : `${offset}`
      return {
        label: `UTC${formattedOffset}:00`,
        value: `${formattedOffset}:00`
      }
    })
  }

  static getDateWithOffset = (date: Date | string): Date => {
    const d = new Date(date)
    return new Date(d.getTime() + d.getTimezoneOffset() * 60000) // Convert to UTC by adjusting the local timezone offset
  }

  /**
   * Returns the current date and time in the format "DD Month YYYY at HH:MM".
   * @param date - The date to format.
   * @returns The current date and time.
   * @example
   * ```ts
   * const date = new Date("2021-01-01T12:00:00");
   * const formattedDate = DateFormatter.getClockFormattedTime(date);
   * formattedDate // {hours: 12, minutes: 0}
   * ```
   */
  static getClockFormattedTime(value: string | undefined): ClockType | undefined {
    if (!value) return undefined

    /** check if the value satisfies the format hh:mm */
    if (!/^\d{1,2}:\d{2}$/.test(value)) throw new Error('Invalid time format')

    const [hour, minute] = value.split(':')
    const hours = parseInt(hour)
    const minutes = parseInt(minute)
    return { hours, minutes }
  }

  /**
   * Returns the current date and time in the format "DD Month YYYY at HH:MM".
   * @param date - The date to format.
   * @returns The current date and time.
   * @example
   * ```ts
   * const date = new Date("2021-01-01T12:00:00");
   * const formattedDate = DateFormatter.getClockFormattedDate('12:00');
   * formattedDate // 2021-01-01T12:00:00.000Z
   * ```
   */
  static getClockFormattedDate(value: string | undefined): Date | undefined {
    const result = DateFormatter.getClockFormattedTime(value)
    if (!result) return undefined
    const { hours, minutes } = result
    return new Date(0, 0, 0, hours, minutes)
  }

  /**
   * Create a date from a time and a date
   * @param time - The time to create the date from
   * @param date - The date to create the date from
   * @returns The date created from the time and the date
   */
  static createDate(time: Time, date?: Date | string): Date {
    return setMinutes(setHours(date ?? new Date(), time.hours), time.minutes)
  }

  /**
   * Returns the current date and time in the format "DD Month YYYY at HH:MM".
   * @param date - The date to format.
   * @returns The current date and time.
   * @example
   * ```ts
   * const date = new Date("2021-01-01T12:00:00");
   * const formattedDate = DateFormatter.manipulateDateByQuarters(date, 1, 'add');
   * formattedDate // 2021-01-01T12:15:00.000Z
   * ```
   */
  static manipulateDateByQuarters(date: Date, quarters: number, mode: 'add' | 'subtract'): Date {
    if (quarters === 0) return new Date(date)

    const newDate = new Date(date)
    // Each quarter is 15 minutes
    const minutes = quarters * 15

    // Add or subtract the minutes to the given date
    if (mode === 'add') return addMinutes(newDate, minutes)
    return subMinutes(newDate, minutes)
  }

  /**
   * Rounds a date to the nearest quarter.
   * @param date - The date to round.
   * @returns The rounded date.
   * @example
   * ```ts
   * const date = new Date("2021-01-01T12:33:00");
   * const roundedDate = DateFormatter.roundToNearestQuarter(date);
   * roundedDate; // 2021-01-01T12:30:00.000Z
   * ```
   */
  static roundToNearestQuarter(date: Date): Date {
    const minutes = date.getMinutes()
    const roundedMinutes = Math.round(minutes / 15) * 15
    return new Date(date.setMinutes(roundedMinutes))
  }

  static manipulateDateToNearestQuarter(
    time: Time,
    quarters: number,
    mode: 'add' | 'subtract'
  ): Time {
    if (quarters === 0) return time
    const result = DateFormatter.roundToNearestQuarter(
      DateFormatter.manipulateDateByQuarters(
        DateFormatter.setTime(new Date(), time),
        quarters,
        mode
      )
    )
    return { hours: result.getHours(), minutes: result.getMinutes() }
  }

  static getFormattedTime(input: Time | string | undefined | null): string {
    if (!input) return ''
    if (typeof input === 'string') return input
    const { hours, minutes } = input
    return `${DateFormatter.HH(hours)}:${DateFormatter.MM(minutes)}`
  }

  static formatTime(time: string): Time | null {
    const [hours, minutes] = time.split(':').map((part) => parseInt(part, 10))
    if (isNaN(hours) || isNaN(minutes)) return null
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null
    return { hours, minutes }
  }

  /**
   * Sets the time of a date.
   * @param date - The date to set the time of.
   * @param time - The time to set the date to.
   * @returns The date with the time set.
   */
  static setTime(date: Date | string, time: Time): Date {
    return setMinutes(setHours(new Date(date), time.hours), time.minutes)
  }

  /**
   * Convert a time from one timezone to another
   * @param time - The time to convert
   * @param fromZone - The timezone of the time
   * @param toZone - The timezone to convert the time to
   * @returns The converted time
   * @example
   * ```ts
   * const convertedTime = TimeUtils.convertTimeZone('10:00', 'Europe/Paris', 'Asia/Beirut')
   * convertedTime // "07:00"
   * ```
   */
  static convertTimeZone(
    time: Time | string,
    fromZone: string,
    toZone: string,
    date?: Date
  ): string {
    const timeString = this.getFormattedTime(time) // "HH:mm"
    const hour = typeof time === 'string' ? Number(timeString.split(':')[0]) : time.hours
    const minute = typeof time === 'string' ? Number(timeString.split(':')[1]) : time.minutes

    if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
      throw new Error(`Invalid time format: ${timeString}`)
    }

    // IMPORTANT: if no date is provided, Luxon will use "today" (in fromZone)
    let dt = date
      ? DateTime.fromJSDate(date, { zone: fromZone })
      : DateTime.now().setZone(fromZone)

    dt = dt.set({ hour, minute, second: 0, millisecond: 0 })

    if (!dt.isValid) {
      throw new Error(
        `Invalid time/date/timezone: ${timeString} in ${fromZone}` +
          (dt.invalidReason ? ` (${dt.invalidReason})` : '')
      )
    }

    return dt.setZone(toZone).toFormat('HH:mm')
  }

  static timeStringToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map((part) => parseInt(part, 10))
    return hours * 60 + minutes
  }

  static isTimeGreaterThanOrEqualTo(start: Time, end: Time): boolean {
    const startTotalMinutes = start.hours * 60 + start.minutes
    const endTotalMinutes = end.hours * 60 + end.minutes

    return startTotalMinutes >= endTotalMinutes
  }

  /**
   * Returns the time duration between two times in the format "Xh Ym".
   * @param start - The start time in "HH:MM" format.
   * @param end - The end time in "HH:MM" format.
   * @returns The time duration in "Xh Ym" format.
   * @example
   * ```ts
   * const duration = getTimeDuration('09:00', '12:30');
   * duration; // "3h 30m"
   * ```
   */
  static getTimeDuration = (start: Time, end: Time): string => {
    const startTotalMinutes = start.hours * 60 + start.minutes
    const endTotalMinutes = end.hours * 60 + end.minutes

    const diff = endTotalMinutes - startTotalMinutes

    const hours = Math.floor(diff / 60)
    const minutes = diff % 60

    return `${hours > 0 ? `${hours}h` : ''}${minutes > 0 ? ` ${minutes}m` : ''}`.trim()
  }

  static getDurationInDays = (start: Date | string, end: Date | string): number => {
    const startDate = new Date(start)
    const endDate = new Date(end)

    // Calculate the difference in milliseconds
    const diffInMs = endDate.getTime() - startDate.getTime()

    // Convert milliseconds to days
    return Math.floor(diffInMs / (1000 * 60 * 60 * 24)) + 1
  }

  /**
   * Formats a number of milliseconds to a time string in the format "MM:SS".
   * @param milliseconds - The number of milliseconds to format.
   * @returns The formatted time string.
   * @example
   * ```ts
   * const time = formatMsToTime(123456)
   * time // "02:03"
   * ```
   */
  static formatMsToTime = (milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }
}

/**
 * Returns the maximum date for a given age.
 * @param age - The age to calculate the max date for.
 * @returns The maximum date for the given age.
 */
export const getMaxDateForAge = (age: number): Date => {
  const currentDate = new Date()
  const year = currentDate.getFullYear() - age
  const month = currentDate.getMonth()
  const day = currentDate.getDate() - 1

  // Return the max date
  return new Date(year, month, day)
}

/**
 * Checks if a date is valid.
 * @param date - The date to check.
 * @returns `true` if the date is valid, `false` otherwise.
 */
export const isValidDate = (date: string | Date): boolean => {
  const parsedDate = new Date(date)
  return parsedDate instanceof Date && !isNaN(parsedDate.getTime())
}

/**
 * Returns the options for the year input.
 * @param type - The type of year options to return.
 * @returns The year options.
 * @example
 * ```ts
 * const yearOptions = getYearOptions();
 * yearOptions // [2024, 2023, 2022, ...]
 * ```
 *
 * ```ts
 * const birthdateYearOptions = getYearOptions('birthdate');
 * birthdateYearOptions // [1990, 1989, 1988, ...]
 * ```
 */
export function getYearOptions(type?: YearOptionsType): number[] {
  const currentYear = new Date().getFullYear()

  // Provide wide, current-relative year ranges so the picker isn't restricted
  // to a hard-coded or very narrow window. The exact spans follow common
  // platform conventions:
  // - birthdate: allow ages from 18 up to 120 years (currentYear - 120 .. currentYear - 18)
  // - appointment: a small +/- range around the current year (currentYear - 5 .. currentYear + 5)
  // - default: generous range (currentYear - 120 .. currentYear + 10)
  let startYear: number
  let endYear: number

  switch (type) {
    case 'birthdate':
      startYear = currentYear - 120
      endYear = currentYear - 18
      break

    case 'appointment':
      startYear = currentYear - 5
      endYear = currentYear + 5
      break

    default:
      startYear = currentYear - 120
      endYear = currentYear + 10
      break
  }

  // Build the array inclusive of startYear..endYear and return newest-first
  const yearsOptions = Array.from(
    { length: endYear - startYear + 1 },
    (_, i) => startYear + i
  ).sort((a, b) => b - a)

  return yearsOptions
}

/**
 * Returns the months in the given locale.
 * @param locale - The locale to get the months in.
 * @returns The months.
 * @example
 * ```ts
 * const months = getMonths();
 * months // ["Janvier", "Février", "Mars", ...]
 * ```
 *
 * ```ts
 * const months = getMonths('en');
 * months // ["January", "February", "March", ...]
 * ```
 */
export function getMonths(locale: 'fr' | 'en' = 'fr'): string[] {
  return Array.from(
    { length: 12 },
    (_, i) =>
      new Date(0, i).toLocaleString(locale, { month: 'long' }).charAt(0).toUpperCase() +
      new Date(0, i).toLocaleString(locale, { month: 'long' }).slice(1)
  )
}

/**
 * Returns the disabled dates for the date picker.
 * @param options - The options to get the disabled dates for.
 * @returns The disabled dates.
 * @example
 * ```ts
 * const disabledDates = getDisabledDates({ disabledPastDates: true, disabledFutureDates: true });
 * disabledDates // { before: new Date() }
 * ```
 */
export function getDisabledDates({
  disabledFutureDates,
  disabledPastDates
}: DisabledDatesOptions): Matcher | undefined {
  if (!disabledPastDates && !disabledFutureDates) return undefined

  const today = new Date()
  today.setHours(0, 0, 0, 0) // Start of today

  return { [disabledPastDates ? 'before' : 'after']: today } as Matcher
}
